import { motion } from "framer-motion";
import { ArrowRight, Star, CheckCircle2, Signal, Wifi, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/siteData";
import { BrowserFrame } from "@/components/mockups/BrowserFrame";
import { MiniDashboard } from "@/components/mockups/MiniDashboard";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  return (
    <section id="home" className="relative overflow-hidden bg-navy-deep">
      {/* Background image + overlays */}
      <div className="absolute inset-0">
        <img
          src={BRAND.heroBg}
          alt=""
          className="h-full w-full object-cover opacity-70"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-navy opacity-90" />
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-techblue/20 blur-3xl" />
        <div className="absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-techblue-glow/20 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-20 pt-32 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:pb-28 lg:pt-40">
        {/* Copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-navy-foreground backdrop-blur"
          >
            <span className="flex h-1.5 w-1.5 rounded-full bg-techblue-glow animate-pulse-glow" />
            {BRAND.tagline}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease }}
            className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-navy-foreground sm:text-5xl lg:text-6xl"
          >
            Manage Your Business Website{" "}
            <span className="text-gradient-silver">Without a Developer.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease }}
            className="mt-6 max-w-xl text-base leading-relaxed text-navy-muted sm:text-lg"
          >
            Anchor Web Systems gives business owners complete control over their website
            through an easy-to-use website management platform. Update services, pricing,
            photos, business information, and content whenever you need — without waiting
            on a developer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25, ease }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <Button asChild variant="cta" size="xl">
              <a href="#demo">
                <CalendarCheck className="h-4 w-4" /> Schedule a Demo
              </a>
            </Button>
            <Button asChild variant="hero" size="xl">
              <a href="#preview">
                See the Platform in Action <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4, ease }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-navy-muted"
          >
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-techblue-glow" /> No coding required
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-techblue-glow" /> Update anytime
            </span>
            <span className="inline-flex items-center gap-2">
              <Star className="h-4 w-4 fill-techblue-glow text-techblue-glow" /> Built to perform
            </span>
          </motion.div>
        </div>

        {/* Visual: website + dashboard */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease }}
          className="relative"
        >
          <div className="relative animate-float-slow">
            <BrowserFrame url="sparkleclean.com" theme="light" className="shadow-elegant">
              <div className="bg-gradient-subtle p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="h-4 w-4 rounded-md bg-gradient-blue" />
                    <div className="h-2 w-16 rounded bg-navy/80" />
                  </div>
                  <div className="flex gap-1.5">
                    <div className="h-1.5 w-8 rounded bg-muted-foreground/30" />
                    <div className="h-1.5 w-8 rounded bg-muted-foreground/30" />
                    <div className="h-1.5 w-10 rounded bg-techblue/70" />
                  </div>
                </div>
                <div className="mt-4 rounded-xl bg-gradient-navy p-4">
                  <div className="h-2 w-24 rounded bg-white/70" />
                  <div className="mt-2 h-1.5 w-40 rounded bg-white/30" />
                  <div className="mt-3 h-5 w-20 rounded-full bg-gradient-blue" />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="rounded-lg border border-border bg-card p-2.5">
                      <div className="h-6 w-6 rounded-md bg-accent" />
                      <div className="mt-2 h-1.5 w-full rounded bg-muted" />
                      <div className="mt-1 h-1.5 w-2/3 rounded bg-muted" />
                    </div>
                  ))}
                </div>
              </div>
            </BrowserFrame>
          </div>

          {/* Floating dashboard */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease }}
            className="absolute -bottom-10 -left-6 w-[62%] animate-float sm:-left-10"
          >
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-navy shadow-elegant ring-1 ring-white/10">
              <MiniDashboard />
            </div>
          </motion.div>

          {/* status chip */}
          <div className="glass absolute -right-3 top-8 hidden items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium text-navy-foreground sm:flex">
            <Signal className="h-3.5 w-3.5 text-techblue-glow" />
            Live
            <Wifi className="h-3.5 w-3.5 text-techblue-glow" />
          </div>
        </motion.div>
      </div>

      {/* bottom fade to white */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-background" />
    </section>
  );
};
